import { useQuery } from "@tanstack/react-query";
import { CONTACT_TEMPLATE_SELECT } from "@west-tennessee-consulting/supabase";
import { getSupabase } from "@/lib/hooks/api/utils";

export function useContactNotes(params?: {
  page?: number;
  limit?: number;
  search?: string;
  contact_id?: string;
  note_type?: string;
  is_internal?: boolean;
}) {
  return useQuery({
    queryKey: ["contact-notes", params],
    queryFn: async () => {
      const supabase = getSupabase();
      let query = supabase.from("contact_notes").select(`
                    *,
                    contacts:contact_id (
                        id,
                        name,
                        company_name,
                        email
                    ),
                    users:app_users(
                        id,
                        first_name,
                        last_name,
                        email
                    )
                `);

      if (params?.contact_id) {
        query = query.eq("contact_id", params.contact_id);
      }
      if (params?.note_type) {
        query = query.eq("note_type", params.note_type);
      }
      if (params?.is_internal !== undefined) {
        query = query.eq("is_internal", params.is_internal);
      }
      if (params?.search) {
        query = query.or(`title.ilike.%${params.search}%,content.ilike.%${params.search}%`);
      }

      if (params?.limit) {
        const offset = (params.page || 0) * params.limit;
        query = query.range(offset, offset + params.limit - 1);
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching contact notes:", error);
        throw new Error(error.message);
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useContactNote(id: string) {
  return useQuery({
    queryKey: ["contact-notes", id],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contact_notes")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching contact note:", error);
        throw new Error(error.message);
      }

      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useContactByEmail(email: string) {
  return useQuery({
    queryKey: ["contacts", "email", email],
    queryFn: async () => {
      if (!email || typeof email !== "string" || email.trim() === "") {
        return null;
      }
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("contacts")
        // Embed linked company (CONTACT_TEMPLATE_SELECT) so compose
        // {{company_name}} + industry/website/rates resolve for a contact
        // auto-matched from the To address.
        .select(CONTACT_TEMPLATE_SELECT)
        .or(`email.eq.${email.trim()},email_aliases.cs.{"${email.trim()}"}`)
        .single();

      if (error) {
        if (error.code === "PGRST116") {
          return null;
        }
        console.error("Error fetching contact by email:", error);
        throw new Error(error.message);
      }

      return data;
    },
    enabled: !!email && typeof email === "string" && email.trim() !== "",
    staleTime: 5 * 60 * 1000,
  });
}
